import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { StateClear, StateResetAll } from 'ngxs-reset-plugin';
import { Observable, of, Subject } from 'rxjs';
import { UserType } from '../models/enum.model';
import { LogInData } from '../models/log-in-data.model';
import { RegisterUserModel } from '../models/register-user.model';
import { ClearBuildingCategories } from '../ngxs/action/building.action';
import { environment } from './../../../environments/environment';
import { UserData } from './../data/user-data.model';
import { IsAuthorized, SetAccount } from './../ngxs/action/account.action';
import { JwtHelperService } from './jwt-helper.service';
import { LocalCacheService } from './local-cache.service';
import { LocalizationService } from './localization.service';
import { NotificationService } from './notification.service';

@Injectable()
export class AuthService {
  public get isAuthorized(): boolean {
    return this.getToken() != null;
  }

  private _hasStorage: boolean;
  private _refreshingSession = false;
  private _userData: UserData = null;
  private email = '';
  hasAirlineUserRole = false;

  constructor(
    private _http: HttpClient,
    private _jwtHelper: JwtHelperService,
    private _router: Router,
    private store: Store,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private localCacheService: LocalCacheService,
    private localizationService: LocalizationService
  ) {
    this._hasStorage = typeof Storage !== 'undefined';
    this._isAuthorized = new Subject<boolean>();
  }

  public _isAuthorized: Subject<boolean>;
  getIsAuthorized(): Observable<boolean> {
    // token exist in storage(browser closed), but expired
    if (
      this.isAuthorized &&
      this.getSecondsFromLastRefresh() > this.getExpireSeconds()
    ) {
      this.logout();
    }

    // browser was closed, but token is still valid
    if (this.isAuthorized && !this._refreshingSession) {
      this.refreshSession();
    }

    this._isAuthorized.next(this.isAuthorized);

    return of(this.isAuthorized);
  }

  getToken(): string {
    return sessionStorage.getItem('habitat_token');
  }

  getTokenTime(): Date {
    return new Date(sessionStorage.getItem('habitat_token_time'));
  }

  removeToken(): void {
    this._userData = null;
    sessionStorage.removeItem('habitat_token');
    sessionStorage.removeItem('habitat_token_expires');
    sessionStorage.removeItem('habitat_token_refresh');
    sessionStorage.removeItem('habitat_token_time');
    sessionStorage.removeItem('habitat_token_type');
    sessionStorage.removeItem('habitat_scope');
  }

  getRefreshToken(): string {
    return sessionStorage.getItem('habitat_token_refresh');
  }

  getExpireSeconds(): number {
    return Number(sessionStorage.getItem('habitat_token_expires'));
  }

  getUserData(): Observable<UserData> {
    if (this._userData) {
      return of(this._userData);
    } else {
      return new Observable<any>((observer) => {
        if (this.isAuthorized) {
          const data = this._jwtHelper.decodeToken();
          this._userData = data;
          observer.next(data);
          observer.complete();
        } else {
          observer.next({});
          observer.complete();
        }
      });
    }
  }

  getUserEmail() {
    return this.email;
  }

  private setUserData(data: any, categories: any) {
    const registerModel = new RegisterUserModel();
    registerModel.email = data.Email;
    registerModel.firstname = data.Firstname;
    registerModel.lastname = data.Lastname;
    registerModel.type = data.UserType as UserType;
    registerModel.categories = categories;

    this.store.dispatch(new SetAccount(registerModel));
    this.store.dispatch(new IsAuthorized(true));

    const url = this.getRedirectUrl();
    this.removeRedirectUrl();

    if (url) {
      this._router.navigate([url]);
    }
  }

  login(model: LogInData): void {
    this._http
      .post(environment.bffUrl + '/api/v1/account/login', {
        Email: model.email,
        Password: model.password,
      })
      .subscribe(
        (response: {
          token: string;
          refreshToken: string;
          isEmailConfirmed: boolean;
          expiration: Date;
          type: string;
          scope: string;
          categories: string[];
        }) => {
          // Not valid
          if (response === null) {
            this.notificationService.warning(
              this.localizationService.translate('login'),
              this.localizationService.translate('invalid-user')
            );
          } else if (!response.isEmailConfirmed) {
            this.notificationService.warning(
              this.localizationService.translate('login'),
              this.localizationService.translate('not-confirmed-user')
            );
          } else {
            if (!response.token) {
              sessionStorage.setItem('habitat_token', '');
              sessionStorage.setItem('habitat_token_expires', '');
              this.store.dispatch(new IsAuthorized(false));
              return false;
            }

            this.store.dispatch(new StateResetAll());

            sessionStorage.setItem('habitat_token', response.token);
            sessionStorage.setItem(
              'habitat_token_refresh',
              response.refreshToken
            );
            sessionStorage.setItem(
              'habitat_token_expires',
              response.expiration.toString()
            );
            sessionStorage.setItem('habitat_token_time', new Date().toString());
            sessionStorage.setItem('habitat_token_type', response.type);
            sessionStorage.setItem('habitat_token_scope', response.scope);

            this.refreshSession();
            this._isAuthorized.next(this.isAuthorized);

            const data = this._jwtHelper.decodeToken(response.token);

            if (data) {
              // Remove all cached data
              this.localCacheService.removeAllKeys();

              this.setUserData(data, response.categories);
              this.notificationService.info(
                this.localizationService.translate('login'),
                this.localizationService.translate('successfull-login')
              );
            } else {
              this.notificationService.warning(
                this.localizationService.translate('login'),
                this.localizationService.translate('invalid-user')
              );
            }
          }
        }
      );
  }

  getSecondsFromLastRefresh(): number {
    return Math.abs(
      (new Date().getTime() - this.getTokenTime().getTime()) / 1000
    );
  }

  refreshSession(): void {
    this._refreshingSession = true;

    const secondsTillRefresh =
      this.getSecondsFromLastRefresh() + 60 < this.getExpireSeconds()
        ? this.getExpireSeconds() - this.getSecondsFromLastRefresh() - 60
        : 0;
    setTimeout(() => {
      this.doRefreshToken().subscribe(
        (state) => {},
        (_) => {
          this.store.dispatch(new IsAuthorized(false));
        }
      );
    }, secondsTillRefresh * 1000);

    this.store.dispatch(new IsAuthorized(true));
  }

  doRefreshToken(callRefreshSession = true): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this.isAuthorized) {
        const refresh = {
          RefreshToken: this.getRefreshToken(),
          ClientId: 'Habitat-Client',
        };

        this._http
          .post(environment.bffUrl + '/api/v1/account/refresh-token', refresh)
          .subscribe(
            (response: {
              token: string;
              refreshToken: string;
              isEmailConfirmed: boolean;
              expiration: Date;
              type: string;
              scope: string;
              categories: string[];
            }) => {
              if (response.token && response.refreshToken) {
                this.removeToken();

                sessionStorage.setItem('habitat_token', response.token);
                sessionStorage.setItem(
                  'habitat_token_refresh',
                  response.refreshToken
                );
                sessionStorage.setItem(
                  'habitat_token_expires',
                  response.expiration.toString()
                );
                sessionStorage.setItem(
                  'habitat_token_time',
                  new Date().toString()
                );

                sessionStorage.setItem('habitat_token_type', response.type);
                sessionStorage.setItem('habitat_token_scope', response.scope);

                const data = this._jwtHelper.decodeToken(response.token);

                if (data) {
                  this.setUserData(data, response.categories);
                } else {
                  this.notificationService.warning(
                    this.localizationService.translate('invalid-token'),
                    this.localizationService.translate(
                      'invalid-token-login-failed'
                    )
                  );

                  this.localCacheService.removeAllKeys();
                }

                this.store.dispatch(new IsAuthorized(true));

                if (callRefreshSession) {
                  // this.refreshSession();
                }
              } else {
                this.logout();
              }
            },
            (message) => {
              this.logout();
            }
          );
      }
    });
  }

  logout() {
    this.removeToken();
    this._isAuthorized.next(this.isAuthorized);
    this.localCacheService.removeAllKeys();
    this.store.dispatch(new IsAuthorized(false));
    // this.store.dispatch(new StateResetAll());
    this.store.dispatch(new ClearBuildingCategories());

    this._router.navigate([`/`]);
  }

  /**
   * Stores the URL so we can redirect after signing in.
   */
  public getRedirectUrl(): string {
    if (this._hasStorage) {
      return sessionStorage.getItem('redirectUrl');
    }
    return null;
  }

  public setRedirectUrl(url: string): void {
    if (this._hasStorage) {
      sessionStorage.setItem('redirectUrl', url);
    }
  }

  public removeRedirectUrl(): void {
    sessionStorage.removeItem('redirectUrl');
  }

  public isUserCarrierAdmin(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.getUserData().subscribe((userData: UserData) => {
        let result = false;

        observer.next(result);
        observer.complete();
      });
    });
  }
}

export interface Token {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  scope: string;
  token_type: string;
}
